import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Music, Users, ArrowRight, Sparkles, Radio, Play } from "lucide-react";
import itzyStudySession from "@/assets/itzy-study-session.jpg";
import itzyGroupPhoto1 from "@/assets/itzy-group-photo-1.webp";
import itzyGroupPhoto2 from "@/assets/itzy-group-photo-2.jpg";

const StudyWithItzy = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Study With ITZY - MIDZY Study Rooms"
        description="Study with ITZY fans worldwide. Join MIDZYs in virtual study rooms with ITZY playlists, themed spaces, and 24/7 study sessions for focused productivity."
        keywords="study with itzy, itzy study room, midzy study, kpop study room, itzy playlist study"
        canonicalUrl="/study-with-itzy/"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <Music className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">CSW STUDY TOGETHER</span>
            </div>
            
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Study With{" "}
              <span className="text-gradient">ITZY</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Join MIDZYs worldwide who study with ITZY's energetic vibes. Stay motivated with their message of self-confidence and achieve your goals together.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start A Room Now <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
            
            {/* Video Section */}
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See how it works</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About ITZY */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground text-center">
              Study with <span className="text-gradient">ITZY</span>
            </h2>
            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
                <p>
                  ITZY is a South Korean girl group formed by JYP Entertainment, known for their empowering messages and energetic performances. The group consists of five members: Yeji, Lia, Ryujin, Chaeryeong, and Yuna. With hits like "DALLA DALLA," "ICY," and "WANNABE," ITZY inspires fans with their message of self-love and confidence.
                </p>
                <p>
                  MIDZYs around the world find motivation in ITZY's positive energy and self-empowerment themes. Studying with ITZY means embracing confidence, staying focused, and believing in yourself while working toward your goals.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Study Session Visual */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                  Study Together with <span className="text-gradient">MIDZYs</span>
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Join ITZY study sessions where members connect online just like you. Experience the same energy and motivation that ITZY brings to their work, now in your study sessions. MIDZYs worldwide gather to support each other while staying productive.
                </p>
              </div>
              <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-all">
                <img 
                  src={itzyStudySession} 
                  alt="ITZY members in virtual study session" 
                  className="w-full h-full object-cover"
                />
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Study Features for <span className="text-gradient">MIDZYs</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                { icon: <Music className="w-7 h-7" />, title: "ITZY Study Playlists", desc: "Study with curated ITZY playlists featuring their empowering songs that keep you motivated and energized throughout your study sessions." },
                { icon: <Users className="w-7 h-7" />, title: "Connect with MIDZYs", desc: "Join study rooms with fellow MIDZYs from around the world who share your passion for ITZY and your dedication to academic success." },
                { icon: <Sparkles className="w-7 h-7" />, title: "Themed Study Rooms", desc: "Create or join ITZY-themed study rooms with custom backgrounds and atmospheres inspired by their iconic concepts and visuals." },
                { icon: <Radio className="w-7 h-7" />, title: "24/7 Study Sessions", desc: "Access ITZY study rooms anytime, anywhere. Stay productive with their empowering energy whenever you need motivation to study." },
              ].map((feature, index) => (
                <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-4">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Group Photo Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Card className="overflow-hidden border-border/50">
              <img 
                src={itzyGroupPhoto2} 
                alt="ITZY members in powerful group photo" 
                className="w-full h-auto object-cover"
              />
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Ready to Study with <span className="text-gradient">ITZY?</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join MIDZYs worldwide and stay motivated with ITZY's empowering energy. Start your study session today.
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Explore CSW Rooms <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StudyWithItzy;